import axiosInstance from "api/axiosInstance";
import { toAdminUserListResponse } from "utils/admin-user-list";

import type { UserDetail, UserAdminManageRequest, UserLevel, UserRestoreResponse, InstructorBrandUpdateRequest, InstructorBrandUpdateResponse, InstructorPublicDetail, AdminUserListParams, AdminUserListResponse } from "api/types/user.types";

const BASE = "/server/buccl_user/api/v1/";

// 빈 검색어를 `search=` 로 보내면 BE 가 빈 문자열 필터로 받아 의미가 흐려진다 —
// 값이 없는 파라미터는 아예 싣지 않는다.
const normalizeListParams = ({ page, page_size, search }: AdminUserListParams): AdminUserListParams => {
  const params: AdminUserListParams = {};
  if (page !== undefined) params.page = page;
  if (page_size !== undefined) params.page_size = page_size;
  const trimmed = search?.trim();
  if (trimmed) params.search = trimmed;
  return params;
};

// ADR-021 — 사용자 식별자
// 관리자용 엔드포인트는 정수 PK 또는 UUID `public_id` 어느 쪽으로도 호출 가능(백엔드 dual-lookup).
// 신규 코드는 `public_id` 를 우선 사용한다.
type UserIdentifier = number | string;

export const usersService = {
  // 회원 상세 조회 — pk(정수) 또는 public_id(UUID) 지원
  getById: (identifier: UserIdentifier) => axiosInstance.get<UserDetail>(`${BASE}user-info/${identifier}/`),

  // 관리자 회원 목록 (DRF pagination envelope).
  // 쿼리스트링은 axios `params` 로 넘긴다 — 직접 보간하면 검색어의 `&`·`#`·공백이
  // 인코딩되지 않아 조용히 잘린다.
  // 응답은 `toAdminUserListResponse` 를 거친다 — BE 롤아웃 창 동안 구 응답(bare array)이
  // 섞여 들어와도 화면이 죽지 않게 하는 **임시** 호환 계층이다 (원문 PII 는 어댑터가 버린다).
  // ⚠️ TODO(제거): BE PR 507 배포 안정화 후 어댑터와 함께 제거한다 —
  // `src/utils/admin-user-list.ts` 상단 주석 참조.
  getList: async (params: AdminUserListParams = {}) => {
    const res = await axiosInstance.get<AdminUserListResponse>(`${BASE}user-info/`, { params: normalizeListParams(params) });
    return { ...res, data: toAdminUserListResponse(res.data) };
  },

  // 회원 검색 — 목록과 같은 엔드포인트/응답이다. BE 가 name/user_id 부분일치로 필터한다.
  // 후보 목록처럼 상한이 필요한 호출부는 `page_size` 를 함께 넘긴다.
  search: (search: string, params: Omit<AdminUserListParams, "search"> = {}) => usersService.getList({ ...params, search }),

  // 관리자: 사용자 상태/역할/개인정보 변경
  // 백엔드(AdminUserManageView)는 수정된 사용자 객체(AdminUserSerializer)를 그대로 반환한다.
  // pk(정수) 또는 public_id(UUID) 지원.
  adminManage: (identifier: UserIdentifier, data: UserAdminManageRequest) => axiosInstance.patch<UserDetail>(`${BASE}admin/users/${identifier}/manage/`, data),

  // 관리자: 강사 소속 브랜드(InstructorProfile.brand) 변경
  // 강사 프로필의 brand 는 관리자 전용 필드 — 별도 단독 엔드포인트로 변경한다.
  // pk(정수) 또는 public_id(UUID) 지원.
  updateInstructorBrand: (identifier: UserIdentifier, data: InstructorBrandUpdateRequest) => axiosInstance.patch<InstructorBrandUpdateResponse>(`${BASE}admin/users/${identifier}/instructor-brand/`, data),

  // 강사 공개 상세 조회
  // 백엔드는 UUID 면 public_id 로 조회, 아니면 로그인 아이디(user_id) 로 조회(dual-lookup).
  // 관리자 토큰으로 호출 시 비공개(is_public=false) 강사 프로필도 조회된다.
  // 매칭 프로필이 없으면 404 (INSTRUCTOR_NOT_FOUND).
  getInstructorDetail: (identifier: string) => axiosInstance.get<InstructorPublicDetail>(`${BASE}instructors/${identifier}/`),

  // 유저 레벨 목록 조회
  getLevels: () => axiosInstance.get<UserLevel[]>(`${BASE}user_levels/`),

  // 관리자: 탈퇴 계정 복구 — pk(정수) 또는 public_id(UUID) 지원
  restore: (identifier: UserIdentifier) => axiosInstance.post<UserRestoreResponse>(`${BASE}restore/${identifier}/`),
};
